import type { InvoiceData, LineItem } from "@/apis/gql/types";
import type { QueryOperator } from "@/types/utils";
import { type ClassValue, clsx } from "clsx";
import Decimal from "decimal.js";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const queryVal = (
  item: string | number | boolean,
  operation: QueryOperator,
  value: string | string[] | number | number[] | boolean | null,
) => {
  switch (operation) {
    case "eq":
      return item === value;
    case "ne":
      return item !== value;
    case "in":
      if (value === null) return false;
      // @ts-ignore
      if (Array.isArray(value)) return value.includes(item);
      return value === item;
    case "gt":
      if (value === null) return false;
      return item > value;
    case "gte":
      if (value === null) return false;
      return item >= value;
    case "lt":
      if (value === null) return false;
      return item < value;
    case "lte":
      if (value === null) return false;
      return item <= value;
  }
};

export function formatCurrencyValue(
  currentValue: number | string | undefined,
): string | number | readonly string[] | undefined {
  // return String(currentValue)?.replace(".", ",");
  const valueToFormat =
    currentValue === undefined || currentValue === null ? 0 : currentValue;

  // Convert to number if it's a string
  const numberValue =
    typeof valueToFormat === "string"
      ? Number.parseFloat(valueToFormat)
      : valueToFormat;

  // Check if the conversion to number was successful
  if (Number.isNaN(numberValue)) {
    return valueToFormat;
  }

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberValue);
}

export function getCurrencySymbol(currency: string) {
  if (!currency) {
    return "";
  }

  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
  })
    .formatToParts()
    .find((part) => part.type === "currency")?.value;
}

export const formatAmount = (amount: number, currencyCode: string) => {
  if (!currencyCode) {
    return "NaN";
  }

  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currencyCode,
  }).format(amount);
};

/**
 * Formats an IBAN by inserting a space after every 4 characters
 * @param iban The IBAN to format (with or without spaces)
 * @returns Formatted IBAN with a space after every 4 characters
 */
export function formatIban(iban: string): string {
  if (!iban) {
    return "";
  }

  const cleanIban = iban.replace(/\s/g, "");
  return cleanIban.replace(/(.{4})(?=.)/g, "$1 ");
}

export function cleanVatPreSave(value: string | number | null | undefined) {
  if (value === "" || value == null || value === undefined) {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  const newValue = Number.parseFloat(value.replace(",", "."));
  if (Number.isNaN(newValue)) {
    return null;
  }

  return newValue;
}

export function calculateBruttoAmount(amount: number, vatRate: number) {
  const amountDecimal = new Decimal(amount);
  const vatRateDecimal = new Decimal(vatRate);

  return amountDecimal
    .times(new Decimal(1).plus(vatRateDecimal.dividedBy(100)))
    .toNumber();
}

export function calculateNettoAmount(amount: number, vatRate: number) {
  const amountDecimal = new Decimal(amount);
  const vatRateDecimal = new Decimal(vatRate);

  return amountDecimal
    .dividedBy(new Decimal(1).plus(vatRateDecimal.dividedBy(100)))
    .toNumber();
}

export function calculateCheckSums(
  totalAmount: number,
  lineItems: LineItem[],
  isNettoInvoice: boolean,
) {
  // margin of error 2 cent
  const ERROR_MARGIN = new Decimal(0.02);

  const total = new Decimal(totalAmount || 0);
  const nettoSums = new Map<number, Decimal>(); // netto subtotals for each vat rate
  const bruttoSums = new Map<number, Decimal>(); // brutto subtotals for each vat rate

  // ---- Try to strategies to calculate the total brutto sum ----

  // Method 1: Get the Brutto total by summing up the line tiem burtto amounts
  // calculate the netto and brutto subtotals of the line items along the way
  let nettoSum = new Decimal(0);
  let bruttoSum = new Decimal(0);
  for (const item of lineItems ?? []) {
    bruttoSum = bruttoSum.plus(item.amount || 0);
    nettoSum = nettoSum.plus(item.nettoAmount || 0);
    const vatRate = item.vatRate || 0;
    const nettoAmount = new Decimal(item.nettoAmount || 0);
    if (nettoSums.has(vatRate)) {
      const currentNettoSum = nettoSums.get(vatRate) ?? new Decimal(0);
      nettoSums.set(vatRate, currentNettoSum.plus(nettoAmount));

      const currentBruttoSum = bruttoSums.get(vatRate) ?? new Decimal(0);
      bruttoSums.set(vatRate, currentBruttoSum.plus(item.amount ?? 0));
    } else {
      nettoSums.set(vatRate, nettoAmount);
      bruttoSums.set(vatRate, new Decimal(item.amount || 0));
    }
  }

  // Method 2: Get the Brutto total by calculating it from the netto subtotals
  let bruttoSumFromNettoSums = new Decimal(0);
  nettoSums.forEach((nettoSum, vatRate) => {
    const vatMultiplier = new Decimal(vatRate).dividedBy(100).plus(1);
    const roundedNettoSum = nettoSum.toDecimalPlaces(2);
    bruttoSumFromNettoSums = bruttoSumFromNettoSums.plus(
      roundedNettoSum.times(vatMultiplier).toDecimalPlaces(2),
    );
  });

  // ---- For brutto invoices the netto subtotal we create are calculate from the butto subtotals ----

  const nettoSumsFromBrutto = new Map<number, Decimal>();
  bruttoSums.forEach((bruttoSum, vatRate) => {
    const vatMultiplier = new Decimal(vatRate).dividedBy(100).plus(1);
    const nettoSum = bruttoSum.dividedBy(vatMultiplier).toDecimalPlaces(2);
    nettoSumsFromBrutto.set(vatRate, nettoSum);
  });
  const nettoSumFromBrutto = Array.from(nettoSumsFromBrutto.values()).reduce(
    (acc, val) => acc.plus(val),
    new Decimal(0),
  );

  // ---- Compare the two brutto total calculation methods ----

  // If either of the two brutto total calc methods are within margin of error, deem correct
  const isCorrectAmount =
    bruttoSum.minus(total).abs().lessThanOrEqualTo(ERROR_MARGIN) ||
    bruttoSumFromNettoSums.minus(total).abs().lessThanOrEqualTo(ERROR_MARGIN);

  // We show the total amount as the sum of the line items, to avoid user confusion about the margin of error
  const displayedBruttoSum =
    isCorrectAmount && totalAmount ? totalAmount : bruttoSum.toNumber();

  return {
    isCorrectAmount,
    bruttoSum: bruttoSum.toNumber(),
    displayedBruttoSum,
    nettoSum: isNettoInvoice
      ? nettoSum.toNumber()
      : nettoSumFromBrutto.toNumber(),
    nettoSums: isNettoInvoice ? nettoSums : nettoSumsFromBrutto,
  };
}

/**
 * Determines if an invoice is from an EU country based on its tax codes (BUs)
 * EU invoices have special BUs like BU18, BU19, BU506, BU507, etc.
 * @param invoice The invoice data to check
 * @returns true if the invoice has any EU BUs
 */
export function isEuInvoice(invoice: InvoiceData): boolean {
  if (!invoice?.lineItems || invoice.lineItems.length === 0) {
    return false;
  }

  // BU codes that indicate EU transactions
  //BU94 is a catch-all BU for 13b invoices: set for invoices that contain a 13b notice
  const euBuCodes = [
    // payable
    18, 19, 702, 701, 506, 507, 6701, 6702, 6506, 6507, 94,
    // receivable
    11, 46, 47, 231, 201, 202, 270,
  ];

  // Log all line items' tax codes

  // Check if any line item has an EU BU
  const result = invoice.lineItems.some((item) => {
    if (!item.taxCode) {
      return false;
    }
    // Check if the taxCode has a datevBu property that matches any EU BU code
    const datevBuNumber = Number.parseInt(item.taxCode.datevBu, 10);
    const isEu = euBuCodes.includes(datevBuNumber);
    return isEu;
  });

  return result;
}

export enum ApplicationView {
  CLIENT = "client",
  ACCOUNTING = "accounting",
}

function getMonthFromDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}`;
}

export const groupInvoicesByMonth = (invoices: InvoiceData[]) => {
  const groupedInvoices = invoices.reduce(
    (acc, invoice) => {
      if (invoice.isBooking) {
        if (!invoice.invoiceDate) {
          return acc;
        }

        const date = new Date(invoice.invoiceDate);

        const monthKey = getMonthFromDate(date);

        if (!acc[monthKey]) {
          acc[monthKey] = [];
        }
        acc[monthKey].push(invoice.invoiceId);
        return acc;
      }

      acc.not_booked.push(invoice.invoiceId);

      return acc;
    },
    { not_booked: [] } as Record<string, string[]>,
  );

  const result =
    groupedInvoices.not_booked.length === 0
      ? Object.fromEntries(
          Object.entries(groupedInvoices).filter(
            ([key]) => key !== "not_booked",
          ),
        )
      : groupedInvoices;

  return result;
};

/**
 * Format percentage for display
 */
export function formatPercentage(percentage: number | null): string {
  if (percentage === null) return "—";
  if (!Number.isFinite(percentage)) return "—"; // Handle NaN, Infinity, -Infinity
  if (percentage === 0) return "0%";
  const sign = percentage > 0 ? "+" : "";
  return `${sign}${percentage.toFixed(1)}%`;
}

/**
 * Calculate year-to-date date range based on fiscal year settings
 */
export function getYTDDateRange(
  fiscalYearStartMonth?: number,
  fiscalYearStartDay?: number,
): { from: Date; to: Date } {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Default to January 1st if no fiscal year set
  const startMonth = fiscalYearStartMonth ?? 1;
  const startDay = fiscalYearStartDay ?? 1;

  // Create fiscal year start date
  let fiscalYearStart = new Date(currentYear, startMonth - 1, startDay);

  // If fiscal year start is in the future, use last year's fiscal year start
  if (fiscalYearStart > today) {
    fiscalYearStart = new Date(currentYear - 1, startMonth - 1, startDay);
  }

  return {
    from: fiscalYearStart,
    to: new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999,
    ),
  };
}
